import React, { useEffect, useState } from 'react'
import { motion, Variants, easeIn } from 'framer-motion'
import Constants from '../../../app/utils/constants'

import cryptonaryPhone1Image from '../../../assets/images/cryptonary/PHONE1.webp'
import cryptonaryPhone2Image from '../../../assets/images/cryptonary/PHONE2.webp'
import cryptonaryPhone3Image from '../../../assets/images/cryptonary/PHONE3.webp'
import cryptonaryPhone4Image from '../../../assets/images/cryptonary/PHONE4.webp'
import cryptonaryPhone5Image from '../../../assets/images/cryptonary/PHONE5.webp'

import cryptonaryWhatWeBuiltCoinImage from '../../../assets/images/cryptonary/whatwebuilt_coin.webp'
import cryptonaryWhatWeBuiltImpactCoinImage from '../../../assets/images/cryptonary/wwb_coin.webp'

import mockupFrame from '../../../assets/images/cryptonary/mockup.webp'

import strategicLeftImage from '../../../assets/images/cryptonary/strategicleft.webp'
import strategicCenterImage from '../../../assets/images/cryptonary/strategicenter.webp'
import strategicRightImage from '../../../assets/images/cryptonary/strategicright.webp'

import colors from '../../theme/color'

// ─── Phone images array ───────────────────────────────────────────────────────
const cryptonaryWhatWeBuiltPhoneImages = [
  cryptonaryPhone1Image,
  cryptonaryPhone2Image,
  cryptonaryPhone3Image,
  cryptonaryPhone4Image,
  cryptonaryPhone5Image,
]

// ─── Types ────────────────────────────────────────────────────────────────────
type ScreenType = {
  id: number
  image: string
  position: 'left' | 'center' | 'right'
}

// ─── Carousel screens ─────────────────────────────────────────────────────────
const INITIAL_SCREENS: ScreenType[] = [
  { id: 1, image: strategicLeftImage,   position: 'left'   },
  { id: 2, image: strategicCenterImage, position: 'center' },
  { id: 3, image: strategicRightImage,  position: 'right'  },
]

// ─── Position styles for carousel ─────────────────────────────────────────────
const screenPositionStyles = {
  left:   { left: '2%',  top: '10%', width: '40%', height: '85%', rotate: 0, scale: 0.88, zIndex: 1, opacity: 1 },
  center: { left: '26%', top: '3%',  width: '47%', height: '93%', rotate: 0, scale: 1,    zIndex: 3, opacity: 1 },
  right:  { left: '62%', top: '10%', width: '40%', height: '85%', rotate: 0, scale: 0.88, zIndex: 1, opacity: 1 },
}

// ─── Per-phone vertical offsets & float delays ────────────────────────────────
const phoneMarginTops  = ['0%', '8.8%', '1.2%', '8.8%', '0%']
const phoneBouncDelays = [0, 0.5, 1.0, 1.5, 2.0]

// Strip margin-top/left/right Tailwind classes so motion y-transform is clean
const stripPhoneImageMargins = (cls: string) =>
  cls.split(' ').filter(c => !c.startsWith('mt-') && !c.startsWith('mr-') && !c.startsWith('ml-')).join(' ')

// ─── Component ────────────────────────────────────────────────────────────────
const WhatWeBuiltSection = () => {
  const {
    cryptonaryBuiltPointsLeft,
    cryptonaryBuiltPointsRight,
    cryptonaryStrategicImpactDescription,
    cryptonaryWhatWeBuiltPhones,
  } = Constants

  const [screens, setScreens] = useState<ScreenType[]>(INITIAL_SCREENS)

  // Rotate carousel every 3.2 s
  useEffect(() => {
    const interval = setInterval(() => {
      setScreens(prev =>
        prev.map(screen => {
          if (screen.position === 'right')  return { ...screen, position: 'center' }
          if (screen.position === 'center') return { ...screen, position: 'left'   }
          return { ...screen, position: 'right' }
        }),
      )
    }, 3200)
    return () => clearInterval(interval)
  }, [])

  // ─── Motion variants ──────────────────────────────────────────────────────
  const lineVariants: Variants = {
    hiddenLeft:  { scaleX: 0, originX: 1 },
    hiddenRight: { scaleX: 0, originX: 0 },
    visible: {
      scaleX: 1,
      transition: { duration: 1.35, ease: [0.22, 1, 0.36, 1], delay: 0.26 },
    },
  }

  const listItemVariants: Variants = {
    hidden: { opacity: 0, y: 24 },
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: index * 0.14, duration: 0.95, ease: [0.22, 1, 0.36, 1] },
    }),
  }

  const topCoinFloatVariants: Variants = {
    initial: { y: 0, rotate: 0 },
    animate: {
      y: [0, -12, 0, -8, 0],
      x: [0, -7, 0, 5, 0],
      rotateX: [0, 15, -12, 6, 0],
      rotate: [0, -4, 0, 3, 0],
      transition: { duration: 7.6, repeat: Infinity, ease: 'easeIn' },
    },
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <section className="relative z-20 w-full overflow-hidden pb-16 sm:pb-20 md:pb-24 lg:pb-28 xl:pb-32 min-[2560px]:pb-48">

      {/* ── Background layers ─────────────────────────────────────────────── */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ backgroundColor: colors.cryptonarySectionBase }}
      />
      <div
        className="pointer-events-none absolute -left-44 top-[18%] hidden h-[640px] w-[500px] rounded-full opacity-[0.10] blur-3xl md:block min-[2560px]:h-[1100px] min-[2560px]:w-[860px]"
        style={{ backgroundColor: colors.cryptonaryGlowPrimary }}
      />
      <div
        className="pointer-events-none absolute -left-20 top-[34%] hidden h-[420px] w-[340px] rounded-full opacity-[0.30] blur-[120px] lg:block min-[2560px]:h-[720px] min-[2560px]:w-[580px]"
        style={{ backgroundColor: colors.cryptonaryGlowSecondary }}
      />
      <div
        className="pointer-events-none absolute -right-44 top-[55%] hidden h-[640px] w-[500px] rounded-full opacity-[0.10] blur-3xl md:block min-[2560px]:h-[1100px] min-[2560px]:w-[860px]"
        style={{ backgroundColor: colors.cryptonaryGlowPrimary }}
      />

      {/* ── Content wrapper ───────────────────────────────────────────────── */}
      <div className="relative z-10 w-full px-4 sm:px-5 md:px-6 lg:px-8 xl:px-10 min-[1536px]:px-14 min-[1681px]:px-10 min-[1800px]:px-20 min-[2200px]:px-28 min-[2560px]:px-16">
        <div className="relative">

          {/* ── Section heading ─────────────────────────────────────────────── */}
          <div className="relative mx-auto flex w-fit items-center gap-3 sm:gap-4 md:gap-5">
            {/* Left line */}
            <motion.span
              variants={lineVariants}
              initial="hiddenLeft"
              whileInView="visible"
              viewport={{ once: false }}
              className="relative h-px w-[44px] sm:w-[56px] md:w-[62px] lg:w-[78px] min-[2560px]:w-[140px]"
              style={{ backgroundColor: `${colors.cryptonaryLineGreen}CC` }}
            >
              <span
                className="absolute -left-[4px] top-1/2 h-[6px] w-[6px] -translate-y-1/2 rounded-full min-[2560px]:h-[10px] min-[2560px]:w-[10px]"
                style={{ backgroundColor: `${colors.cryptonaryLineMint}E6` }}
              />
            </motion.span>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
              className="font-gilroy-bold text-[1.6rem] leading-none sm:text-[1.9rem] md:text-[2.2rem] lg:text-[2.5rem] xl:text-[2.8rem] min-[2560px]:text-[5rem]"
              style={{ color: colors.cryptonaryAccentGreen }}
            >
              What We Built
            </motion.h2>

            {/* Right line */}
            <motion.span
              variants={lineVariants}
              initial="hiddenRight"
              whileInView="visible"
              viewport={{ once: false }}
              className="relative h-px w-[44px] sm:w-[56px] md:w-[62px] lg:w-[78px]   min-[2560px]:w-[140px]"
              style={{ backgroundColor: `${colors.cryptonaryLineGreen}CC` }}
            >
              <span
                className="absolute -right-[4px] top-1/2 h-[6px] w-[6px] -translate-y-1/2 rounded-full min-[2560px]:h-[10px] min-[2560px]:w-[10px]"
                style={{ backgroundColor: `${colors.cryptonaryLineMint}E6` }}
              />
            </motion.span>
          </div>

          {/* ── Phones ──────────────────────────────────────────────────────── */}
          <div className="relative mx-auto mt-8 w-full sm:mt-10 md:mt-10 lg:mt-10 min-[1800px]:mt-12 min-[2200px]:mt-14 min-[2560px]:mt-20">

            {/* Phone strip */}
            <div className="relative z-10 mx-auto w-full px-0 sm:px-1 md:px-2 lg:px-6 min-[1536px]:px-10 min-[1681px]:px-6 min-[1800px]:px-12 min-[2200px]:px-20">
              <div className="flex w-full flex-nowrap items-start justify-center
                gap-[2px]
                sm:gap-1
                md:gap-2
                lg:gap-4
                xl:gap-5
                min-[1900px]:gap-7
                min-[2560px]:gap-10">

                {cryptonaryWhatWeBuiltPhones.map((phone, index) => (
                  <div
                    key={phone.alt}
                    className="relative flex min-w-0 shrink-[1] grow-0 basis-[20%] flex-col items-center"
                    style={{ marginTop: phoneMarginTops[index] }}
                  >

                    {/* ── Floating coin — anchored to first phone top ── */}
                    {index === 0 && (
                      <motion.div
                        variants={topCoinFloatVariants}
                        initial="initial"
                        animate="animate"
                        className="pointer-events-none absolute z-20
                          -top-[10%] -left-[22%]  w-[52px]
                          sm:-top-[10%] sm:-left-[24%] sm:w-[70px]
                          md:-top-[25%] md:-left-[26%] md:w-[82px]
                          lg:-top-[40%] lg:-left-[18%] lg:w-[100px]
                          xl:w-[118px]
                          min-[2560px]:w-[200px]"
                      >
                        <motion.img
                          initial={{ opacity: 0, x: 24, y: 16, scale: 0.88 }}
                          whileInView={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                          viewport={{ once: false, amount: 0.25 }}
                          transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
                          src={cryptonaryWhatWeBuiltCoinImage}
                          alt="Bitcoin coin"
                          className="w-full object-contain"
                          style={{
                            filter: `drop-shadow(0 14px 26px ${colors.cryptonaryBuiltCoinGlow}6B)`,
                            transformPerspective: 1200,
                          }}
                        />
                      </motion.div>
                    )}

                    {/* Reveal wrapper */}
                    <motion.div
                      className="flex w-full flex-col items-center"
                      initial={{ opacity: 0, scale: 0.92, filter: 'blur(3px)' }}
                      whileInView={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                      viewport={{ once: false, amount: 0.2 }}
                      transition={{ duration: 0, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {/* Float bounce */}
                      <motion.div
                        animate={{ y: [0, -20, 0] }}
                        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeIn', delay: phoneBouncDelays[index] }}
                      >
                        <img
                          src={cryptonaryWhatWeBuiltPhoneImages[index]}
                          alt={phone.alt}
                          className={`block w-full object-contain
                            max-h-[min(38vw,240px)]
                            sm:max-h-[min(36vw,320px)]
                            md:max-h-[min(32vw,400px)]
                            lg:max-h-[min(16vw,520px)]
                            xl:max-h-[min(17vw,640px)]
                            2xl:max-h-[min(18vw,760px)]
                            min-[1900px]:max-h-[min(20vw,860px)]
                            min-[2560px]:max-h-[min(18vw,1060px)]
                            ${stripPhoneImageMargins(phone.className)}`}
                        />
                      </motion.div>
                    </motion.div>

                  </div>
                ))}

              </div>
            </div>
          </div>

          {/* ── Built points ────────────────────────────────────────────────── */}
          <div className="mx-auto mt-6 w-full
            px-1
            sm:px-2 sm:mt-7
            md:mt-8 md:px-4
            lg:px-6
            min-[1536px]:px-10 min-[1681px]:px-6 min-[1800px]:px-12 min-[1800px]:mt-10
            min-[2200px]:px-20 min-[2200px]:mt-12
            min-[2560px]:mt-16">
            <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 md:gap-x-6 md:gap-y-4 lg:gap-x-8 lg:gap-y-5 min-[2560px]:gap-x-12 min-[2560px]:gap-y-7">

              {/* Left column */}
              <ul className="space-y-3 sm:space-y-4 min-[2560px]:space-y-6">
                {cryptonaryBuiltPointsLeft.map((point, index) => (
                  <motion.li
                    key={point}
                    custom={index}
                    variants={listItemVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.35 }}
                    className="relative overflow-hidden rounded-full
                      px-5 py-3
                      sm:px-6 sm:py-3.5
                      md:px-7 md:py-4
                      2xl:px-8 2xl:py-4
                      min-[1900px]:px-10 min-[1900px]:py-5
                      min-[2560px]:px-14 min-[2560px]:py-7"
                    style={{
                      backgroundImage: `linear-gradient(90deg, ${colors.cryptonaryAccentGreen}17 0%, ${colors.cryptonaryAccentGreen}05 50%, transparent 100%)`,
                    }}
                  >
                    <span
                      className="pointer-events-none absolute inset-0 rounded-full border"
                      style={{
                        borderColor: `${colors.cryptonaryBuiltBorder}6B`,
                        WebkitMaskImage: `linear-gradient(90deg, ${colors.black} 0%, ${colors.black}E6 34%, ${colors.black}AD 58%, ${colors.black}66 78%, ${colors.black}24 92%, ${colors.black}00 100%)`,
                        maskImage:       `linear-gradient(90deg, ${colors.black} 0%, ${colors.black}E6 34%, ${colors.black}AD 58%, ${colors.black}66 78%, ${colors.black}24 92%, ${colors.black}00 100%)`,
                      }}
                    />
                    <p
                      className="relative flex items-start gap-2 font-gilroy-medium leading-[1.3]
                        text-[0.92rem]
                        sm:text-[1rem]
                        md:text-[1.08rem]
                        lg:text-[1.12rem]
                        2xl:text-[1.22rem]
                        min-[1900px]:text-[1.42rem]
                        min-[2560px]:text-[2rem]"
                      style={{ color: colors.cryptonaryTextBuiltBody }}
                    >
                      <span className="mt-[0.42rem] h-[5px] w-[5px] shrink-0 rounded-full bg-white min-[2560px]:mt-[0.6rem] min-[2560px]:h-[7px] min-[2560px]:w-[7px]" />
                      <span>{point}</span>
                    </p>
                  </motion.li>
                ))}
              </ul>

              {/* Right column */}
              <ul className="space-y-3 sm:space-y-4 min-[2560px]:space-y-6">
                {cryptonaryBuiltPointsRight.map((point, index) => (
                  <motion.li
                    key={point}
                    custom={index + cryptonaryBuiltPointsLeft.length}
                    variants={listItemVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.35 }}
                    className="relative overflow-hidden rounded-full
                      px-5 py-3
                      sm:px-6 sm:py-3.5
                      md:px-7 md:py-4
                      2xl:px-8 2xl:py-4
                      min-[1900px]:px-10 min-[1900px]:py-5
                      min-[2560px]:px-14 min-[2560px]:py-7"
                    style={{
                      backgroundImage: `linear-gradient(90deg, ${colors.cryptonaryAccentGreen}17 0%, ${colors.cryptonaryAccentGreen}05 50%, transparent 100%)`,
                    }}
                  >
                    <span
                      className="pointer-events-none absolute inset-0 rounded-full border"
                      style={{
                        borderColor: `${colors.cryptonaryBuiltBorder}6B`,
                        WebkitMaskImage: `linear-gradient(90deg, ${colors.black} 0%, ${colors.black}E6 34%, ${colors.black}AD 58%, ${colors.black}66 78%, ${colors.black}24 92%, ${colors.black}00 100%)`,
                        maskImage:       `linear-gradient(90deg, ${colors.black} 0%, ${colors.black}E6 34%, ${colors.black}AD 58%, ${colors.black}66 78%, ${colors.black}24 92%, ${colors.black}00 100%)`,
                      }}
                    />
                    <p
                      className="relative flex items-start gap-2 font-gilroy-semibold leading-[1.3]
                        text-[0.92rem]
                        sm:text-[1rem]
                        md:text-[1.08rem]
                        lg:text-[1.12rem]
                        2xl:text-[1.22rem]
                        min-[1900px]:text-[1.42rem]
                        min-[2560px]:text-[2rem]"
                      style={{ color: colors.cryptonaryTextBuiltBody }}
                    >
                      <span className="mt-[0.42rem] h-[5px] w-[5px] shrink-0 rounded-full bg-white min-[2560px]:mt-[0.6rem] min-[2560px]:h-[7px] min-[2560px]:w-[7px]" />
                      <span>{point}</span>
                    </p>
                  </motion.li>
                ))}
              </ul>

            </div>
          </div>

          {/* ── Strategic Impact ────────────────────────────────────────────── */}
          <div className="relative mx-auto w-full
            mt-10 max-w-full
            sm:mt-12
            md:mt-14 md:max-w-[900px]
            lg:max-w-[1100px]
            xl:max-w-[1160px]
            min-[2560px]:mt-28 min-[2560px]:max-w-[2100px]">

            <div className="grid items-center gap-8
              grid-cols-1
              md:grid-cols-[0.96fr_1.04fr] lg:gap-10
              min-[2560px]:gap-20">

              {/* ── Left: Phone carousel ──────────────────────────────────── */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 1.1, ease: easeIn }}
                className="relative"
              >
                <div
                  className="pointer-events-none absolute inset-0 -z-10 blur-2xl"
                  style={{
                    backgroundImage: `radial-gradient(circle at 45% 42%, ${colors.cryptonaryAccentGreen}38 0%, ${colors.cryptonaryAccentGreen}0F 42%, ${colors.cryptonaryAccentGreen}00 76%)`,
                  }}
                />

                <motion.div className="relative mx-auto w-full max-w-[760px]
                  h-[280px]
                  sm:h-[340px]
                  md:h-[400px]
                  lg:h-[480px]
                  xl:h-[520px]
                  2xl:h-[560px]
                  min-[2560px]:h-[900px]">

                  {screens.map((screen) => {
                    const positionStyle = screenPositionStyles[screen.position]
                    return (
                      <motion.div
                        key={screen.id}
                        animate={{
                          left:    positionStyle.left,
                          top:     positionStyle.top,
                          width:   positionStyle.width,
                          height:  positionStyle.height,
                          rotate:  positionStyle.rotate,
                          scale:   positionStyle.scale,
                          opacity: positionStyle.opacity,
                          zIndex:  positionStyle.zIndex,
                        }}
                        transition={{ type: 'spring', stiffness: 90, damping: 18, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute overflow-hidden rounded-[1.2rem] bg-black shadow-[0_18px_40px_rgba(0,0,0,0.45)]
                          sm:rounded-[1.4rem]
                          md:rounded-[1.6rem]
                          lg:rounded-[1.8rem]
                          min-[2560px]:rounded-[2.8rem]"
                        style={{ transformOrigin: 'center center' }}
                      >
                        <motion.img
                          src={screen.image}
                          alt="Phone screen"
                          className="h-full w-full object-cover"
                          animate={{ scale: screen.position === 'center' ? 1 : 1 }}
                          transition={{ duration: 1.2 }}
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20" />
                      </motion.div>
                    )
                  })}

                  {/* Mockup overlay frame */}
                  <motion.img
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    src={mockupFrame}
                    alt="Three phone mockup frame"
                    className="pointer-events-none absolute left-1/2 top-[1%] hidden lg:block z-20 -translate-x-1/2 object-contain
                     
                      lg:w-[51%]
                      xl:w-[50%]
                      min-[2560px]:w-[52%]"
                  />
                </motion.div>
              </motion.div>

              {/* ── Right: Text content ───────────────────────────────────── */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
                className="relative px-0 lg:px-0 text-center md:text-start"
              >
                {/* Floating impact coin */}
                <motion.img
                  animate={{
                    y:      [0, -10, 0, -6, 0],
                    x:      [0,   5, 0, -4, 0],
                    scale:  [1, 1.05, 1, 0.97, 1],
                    rotate: [0,   6, 0,  -5, 0],
                  }}
                  transition={{ duration: 7, repeat: Infinity, ease: 'easeIn' }}
                  src={cryptonaryWhatWeBuiltImpactCoinImage}
                  alt="Strategic impact coin"
                  className="pointer-events-none absolute object-contain
                     -right-[2%] top-[95%] w-[80px]
                    md:-right-[4%] md:top-[90%] md:w-[90px]
                    lg:w-[110px]
                    xl:w-[120px]
                    min-[2560px]:w-[200px]"
                />

                {/* Heading */}
                <motion.h3
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.35 }}
                  transition={{ duration: 0.92, ease: easeIn }}
                  className="font-gilroy-extrabold leading-[1.03]
                    text-[1.8rem]
                    sm:text-[2rem]
                    md:text-[2.2rem]
                    lg:text-[2.5rem]
                    xl:text-[2.8rem]
                    min-[2560px]:text-[4.8rem]"
                >
                  <span style={{ color: colors.cryptonaryAccentGreen }}>Strategic Impact</span>
                  <br />
                  <span className="text-white">Delivered</span>
                </motion.h3>

                {/* Body text */}
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 0.8, y: 0 }}
                  viewport={{ once: false, amount: 0.35 }}
                  transition={{ duration: 0.98, ease: easeIn, delay: 0.12 }}
                  className="mt-4 max-w-[640px] font-gilroy-medium opacity-80 leading-[1.48]
                    text-[0.9rem]
                    sm:text-[0.95rem]
                    md:text-[1rem]
                    lg:text-[1.02rem]
                    xl:text-[1.06rem]
                    min-[2560px]:text-[1.8rem] min-[2560px]:mt-8 min-[2560px]:max-w-[1200px]"
                  style={{ color: colors.cryptonaryTextImpactBody }}
                >
                  {cryptonaryStrategicImpactDescription}
                </motion.p>
              </motion.div>

            </div>
          </div>

        </div>
      </div>
    </section>
  )
}

export default WhatWeBuiltSection